import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Mail, Trash2, Crown, Users as UsersIcon, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ManageUsers = ({ orgId, orgRole }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agente');

  useEffect(() => {
    loadMembers();
  }, [orgId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const [m, o] = await Promise.all([
        axios.get(`${API}/organizations/${orgId}/members`),
        axios.get(`${API}/organizations`)
      ]);
      setMembers(m.data.members || []);
      const myOrg = (o.data || []).find(x => (x.organization_id || x.org_id) === orgId);
      setPending(myOrg?.pending_invites || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
    setLoading(false);
  };

  const handleCancelInvite = async (email) => {
    if (!window.confirm(`¿Cancelar la invitación a ${email}?`)) return;
    try {
      await axios.post(`${API}/organizations/${orgId}/cancel-invite`, { email });
      toast.success('Invitación cancelada');
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) {
      toast.error('Ingresa un email');
      return;
    }

    try {
      await axios.post(`${API}/organizations/${orgId}/invite`, {
        email: inviteEmail,
        role: inviteRole
      });
      
      toast.success('✅ Invitación enviada');
      setInviteEmail('');
      setShowInviteForm(false);
      loadMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar invitación');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('¿Eliminar este usuario de la organización?')) return;

    try {
      await axios.delete(`${API}/organizations/${orgId}/members/${memberId}`);
      toast.success('Usuario eliminado');
      loadMembers();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/organizations/${orgId}/members/${memberId}/role`, {
        role: newRole
      });
      toast.success('Rol actualizado');
      loadMembers();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`[${status}] ${detail}`);
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      owner: { color: 'bg-yellow-500', icon: Crown, label: 'PROPIETARIO' },
      admin: { color: 'bg-red-500', icon: Crown, label: 'ADMIN' },
      lider: { color: 'bg-blue-500', icon: Shield, label: 'LÍDER' },
      agente: { color: 'bg-green-500', icon: UsersIcon, label: 'AGENTE' }
    };
    
    const badge = badges[role] || badges.agente;
    const Icon = badge.icon;
    
    return (
      <span className={`${badge.color} text-white px-3 py-1 text-xs font-bold flex items-center gap-1 border-2 border-black`}>
        <Icon size={12} /> {badge.label}
      </span>
    );
  };

  // Filter members based on current user role
  const visibleMembers = members.filter(member => {
    if (orgRole === 'owner') return true;
    if (orgRole === 'admin') return true;
    if (orgRole === 'lider') {
      return member.role === 'agente';
    }
    return false;
  });

  const canManage = orgRole === 'owner' || orgRole === 'admin' || orgRole === 'lider';

  // Calculate stats by ROLES (not subscription plans)
  const adminCount = members.filter(m => m.role === 'owner' || m.role === 'admin').length;
  const liderCount = members.filter(m => m.role === 'lider').length;
  const agenteCount = members.filter(m => m.role === 'agente').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2" style={{ color: '#e8e6e0', fontFamily: 'var(--font-display)' }}>
          <UsersIcon size={20} style={{ color: '#c9a84c' }} />
          Gestión de Usuarios
        </h2>
        {['owner','admin'].includes(orgRole) && (
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="px-4 py-2.5 font-semibold flex items-center justify-center gap-2 rounded-lg transition-opacity hover:opacity-80 w-full md:w-auto text-sm"
            style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)', fontFamily: 'var(--font-body)' }}
          >
            <UserPlus size={16} />
            Invitar Usuario
          </button>
        )}
      </div>

      {/* Stats Cards - By ROLE */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { label: 'Administradores', count: adminCount, color: '#ef4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.25)' },
          { label: 'Líderes', count: liderCount, color: '#60a5fa', bg: 'rgba(96,165,250,0.1)', border: 'rgba(96,165,250,0.25)' },
          { label: 'Agentes', count: agenteCount, color: '#34d399', bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.25)' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl relative overflow-hidden" style={{ background: s.bg, border: `1px solid ${s.border}` }}>
            <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-body)' }}>{s.label}</p>
            <p className="text-3xl font-bold" style={{ color: s.color, fontFamily: 'var(--font-display)' }}>{s.count}</p>
          </div>
        ))}
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="rounded-xl p-5 border" style={{ background: '#0d0d14', borderColor: 'rgba(201,168,76,0.25)' }}>
          <p className="font-semibold mb-4 flex items-center gap-2 text-sm" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)', letterSpacing: '0.06em' }}>
            <Mail size={16} /> INVITAR NUEVO USUARIO
          </p>
          <div className="grid gap-3">
            <div>
              <label className="text-xs mb-1.5 block tracking-widest" style={{ color: '#7a7870' }}>EMAIL</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="usuario@ejemplo.com"
                className="w-full px-3 py-2.5 rounded-lg text-sm border outline-none"
                style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }} />
            </div>
            <div>
              <label className="text-xs mb-1.5 block tracking-widest" style={{ color: '#7a7870' }}>ROL</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg text-sm border outline-none"
                style={{ background: '#050508', color: '#e8e6e0', borderColor: 'rgba(255,255,255,0.1)' }}>
                {orgRole === 'owner' && <option value="admin">Admin</option>}
                {(orgRole === 'owner' || orgRole === 'admin') && <option value="lider">Líder</option>}
                <option value="agente">Agente</option>
              </select>
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <button onClick={handleInvite}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-opacity hover:opacity-80"
                style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.35)' }}>
                Enviar Invitación
              </button>
              <button onClick={() => setShowInviteForm(false)}
                className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-opacity hover:opacity-70"
                style={{ background: 'rgba(255,255,255,0.04)', color: '#7a7870', border: '1px solid rgba(255,255,255,0.08)' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Invites */}
      {['owner','admin'].includes(orgRole) && pending.length > 0 && (
        <div className="rounded-xl p-4 border" style={{ background: 'rgba(201,168,76,0.06)', borderColor: 'rgba(201,168,76,0.25)' }}>
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#c9a84c', fontFamily: 'var(--font-display)' }}>
            <Mail size={15} /> Invitaciones pendientes ({pending.length})
          </h3>
          <div className="space-y-2">
            {pending.map(email => (
              <div key={email} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <span className="text-sm" style={{ color: '#e8e6e0' }}>{email}</span>
                <button data-testid={`cancel-invite-${email}`} onClick={() => handleCancelInvite(email)}
                  className="px-3 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-opacity hover:opacity-80"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <Trash2 size={12} /> Cancelar
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Members List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#c9a84c', borderTopColor: 'transparent' }} />
        </div>
      ) : visibleMembers.length === 0 ? (
        <div className="rounded-xl p-12 text-center border" style={{ background: '#0d0d14', borderColor: 'rgba(255,255,255,0.07)' }}>
          <UsersIcon size={36} className="mx-auto mb-4" style={{ color: '#c9a84c', opacity: 0.3 }} />
          <p className="text-sm" style={{ color: '#7a7870' }}>No hay usuarios en esta organización</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {visibleMembers.map(member => (
            <div key={member.user_id}
              className="rounded-xl p-4 transition-all"
              style={{ background: 'linear-gradient(135deg, #1a1a24 0%, #131318 100%)', border: '1px solid rgba(255,255,255,0.09)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)' }}
            >
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {member.picture ? (
                      <img src={member.picture} alt={member.name}
                        className="w-10 h-10 rounded-full object-cover"
                        style={{ border: '1px solid rgba(201,168,76,0.35)' }}
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      style={{ display: member.picture ? 'none' : 'flex', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontFamily: 'var(--font-display)' }}
                      className="w-10 h-10 rounded-full items-center justify-center font-bold text-base"
                    >
                      {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{member.name || 'Sin nombre'}</h3>
                      <p className="text-gray-400 text-sm">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    {getRoleBadge(member.role)}
                    <span className="text-gray-500 text-sm">
                      Unido: {new Date(member.joined_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                {orgRole === 'owner' && member.user_id !== user?.user_id && (
                  <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                    {member.role !== 'owner' && (
                      <select
                        value={member.role}
                        onChange={(e) => handleChangeRole(member.user_id, e.target.value)}
                        className="bg-gray-700 text-white px-3 py-2 border-2 border-gray-600 font-bold text-sm w-full md:w-auto"
                      >
                        <option value="agente">Agente</option>
                        <option value="lider">Líder</option>
                        <option value="admin">Admin</option>
                      </select>
                    )}
                    <button
                      onClick={() => handleRemoveMember(member.user_id)}
                      className="px-4 py-2 bg-red-500 text-white font-bold flex items-center justify-center gap-1 hover:bg-red-600 border-2 border-black w-full md:w-auto"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};